import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { UserStatesSelector } from "@/components/ui/state-selector";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Loader2, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { TEAM_TYPE_BADGE_CLASS } from "@/lib/teamTypes";
import {
  EPC_PARTNER,
  INSTALLER_PARTNER,
  addMembership,
  addableMembershipTypes,
  addableOptions,
  membershipTypeLabel,
  membershipsFromUser,
  membershipsToTeamIds,
  type Membership,
} from "@/lib/userTeamMemberships";
import { User } from "@/types";
import { effectiveRoleId } from "@/lib/effectiveRole";
import { supabase } from "@/lib/supabaseClient";
import { useTenant } from "@/contexts/TenantContext";
import { SlidePanel } from "@/components/ui/slide-panel";
import { useToast } from "@/components/ui/use-toast";
import { UserDocuments } from "./UserDocuments";
import { CommissionRolePermissionsNote } from "./CommissionRolePermissionsNote";
import { PersonRoleCard } from "./PersonRoleCard";
import { usePermissions } from "@/contexts/PermissionContext";
import { canAdministerRoles } from "@/lib/rolesAdministration";
import { passwordResetService } from "@/services/passwordResetService";
import { accessEmailToast } from "@/lib/welcomeEmailNotice";

type UserFormProps = {
  initialData?: Partial<User>;
  roles: Array<{ id: string; name: string }>;
  managers: Array<{ id: string; first_name: string; last_name: string }>;
  allTeams: Array<{ id: string; name: string; type?: string }>;
  onSubmit: (userData: Omit<User, "id">) => Promise<{ success: boolean }>;
  onCancel: () => void;
  isSubmitting: boolean;
  addUserError: string | null;
  onEmailChange?: (email: string) => Promise<{ exists: boolean }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserRole?: string;
  canResetPassword?: boolean;
  /** RBAC-4.1: a role changed from the record's Role card, which saves on its own. */
  onRoleChanged?: () => void;
  /** RBAC-4.3: their access was removed from the Role card; the panel should close. */
  onAccessRemoved?: () => void;
};

export const UserForm: React.FC<UserFormProps> = ({
  initialData,
  roles,
  managers,
  allTeams,
  onSubmit,
  onCancel,
  isSubmitting,
  addUserError,
  onEmailChange,
  open,
  onOpenChange,
  currentUserRole,
  canResetPassword = false,
  onRoleChanged,
  onAccessRemoved,
}) => {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const isEdit = !!initialData?.id;
  const { permissions: heldPermissions } = usePermissions();
  /**
   * RBAC-4.1. On an existing person, a `roles.administer` holder changes the role from the Role card,
   * which previews and saves through `assign_person_role()` on its own. The form then never sends
   * `role_id`, so `update-admin-user` leaves both role systems alone and a profile edit cannot move a
   * role. Everyone else, and Add User, keep the select.
   */
  const roleOnRecord = isEdit && canAdministerRoles(heldPermissions);

  const [isEmailChecking, setIsEmailChecking] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);

  // Sends to the SAVED address, not the one in the form, so an unsaved edit to the email field
  // cannot redirect a reset link to someone else.
  const handleSendResetEmail = async () => {
    const savedEmail = initialData?.email;
    if (!savedEmail) return;
    setIsSendingResetEmail(true);
    try {
      const { emailSent } = await passwordResetService.sendResetEmail(savedEmail);
      toast(accessEmailToast("reset", savedEmail, emailSent));
    } catch (error) {
      toast({
        title: "Password reset email not sent",
        description: error instanceof Error ? error.message : "Something went wrong. Try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingResetEmail(false);
    }
  };

  /**
   * ⚠️ SUB-3.8. WHICH ROLES THIS CALLER MAY ACTUALLY HAND OUT.
   *
   * `roles` is every row of the table, because `roles` carries `USING (true)` so
   * that the role-check helpers can read it. So this dropdown offered
   * "Superadmin" and "Agency Admin" to anybody who could open the dialog, and
   * nothing downstream refused the result: `trg_guard_privileged_user_role`
   * exempts `auth.uid() IS NULL`, which is `service_role`, which is what
   * `create-admin-user` runs as. F-15's fix says so in as many words and names
   * the admin-user Edge Functions as the reason for the exemption. Selecting
   * "Superadmin" from a dropdown minted a platform superadmin.
   *
   * The write is refused now (`requireRoleAssignable`, added to
   * `create-admin-user` by this story). This narrows the OFFER to match.
   *
   * `null` means "not answered yet", which renders as the unfiltered list for the
   * one paint before the answer arrives. That is deliberate: an empty dropdown
   * while a request is in flight reads as "this account has no roles", and the
   * server is the control either way.
   */
  const [assignableRoleIds, setAssignableRoleIds] = useState<Set<string> | null>(
    null
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      // Asked of the database, never filtered on role names here.
      // `is_privileged_role` is the ONE definition of "privileged" and the
      // `user_roles` policies, the `users` trigger and `require_caller.ts` all
      // defer to it. A second definition in TypeScript would drift the moment a
      // role is added, silently, in the permissive direction.
      const { data, error } = await supabase.rpc("assignable_role_ids");
      if (cancelled) return;

      if (error) {
        // Left unfiltered rather than emptied: the server refuses a privileged
        // role regardless, and blanking the only required field in the form
        // would break ordinary user creation for everybody on an RPC hiccup.
        console.error(
          "[UserForm] Could not resolve assignable roles, so the full list is offered. The server still refuses a privileged role:",
          error.message
        );
        setAssignableRoleIds(null);
        return;
      }

      setAssignableRoleIds(
        new Set((data ?? []).map((id: number | string) => String(id)))
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  /**
   * The dropdown's options. The role the user ALREADY holds is always kept, even
   * when it is one this caller could not assign: dropping it would render the
   * select empty on an existing Agency Admin and invite a save that silently
   * changed their role. `update-admin-user` refuses that change anyway, on both
   * the old role and the new one.
   */
  /**
   * USR-1.3. WHICH ROLE THIS FORM OPENS ON, resolved the same way the Users table
   * decides what to display.
   *
   * This used to read `initialData.role_id`, the legacy column, while the table beside
   * it rendered `user_roles` through `effectiveRole()`. When those disagreed the form
   * showed one role and the row showed another. That was merely confusing until
   * `update-admin-user` gained Step 5b; from then on saving ANY field wrote the form's
   * stale value into both role systems, so editing a phone number could move a role.
   * Seven accounts were in that state on 2026-08-14 and `20260814g` had to reconcile
   * them before the write path was safe to deploy.
   *
   * ⚠️ `ambiguous` is a real state, not a defensive branch. A person can hold several
   * roles in one account, `effectiveRoleId` refuses to pick between them because this
   * codebase defines no precedence, and `planTenantRoleWrite` deletes every own-role
   * that is not the one submitted. So the select is left EMPTY and the save button
   * blocked until someone chooses, rather than seeding one and quietly dropping the
   * rest on Save.
   */
  const seededRole = useMemo(
    () =>
      effectiveRoleId({
        roleIdsInThisTenant: initialData?.role_ids_in_tenant,
        legacyRoleId: initialData?.role_id,
      }),
    [initialData?.role_ids_in_tenant, initialData?.role_id]
  );

  const roleOptions = useMemo(() => {
    if (!assignableRoleIds) return roles;
    // EVERY role held here is kept, not just the seeded one. Dropping an unassignable
    // role the person already holds would render the select empty on an existing Agency
    // Admin and invite a save that silently changed them. `update-admin-user` refuses
    // that change anyway, on both the old role and the new one.
    const held = new Set(
      (seededRole.ids.length > 0
        ? seededRole.ids
        : seededRole.roleId !== null
          ? [seededRole.roleId]
          : []
      ).map(String)
    );
    return roles.filter(
      (role) => assignableRoleIds.has(String(role.id)) || held.has(String(role.id))
    );
  }, [roles, assignableRoleIds, seededRole]);

  const [formData, setFormData] = useState<
    Omit<User, "id"> & {
    }
  >({
    first_name: "",
    last_name: "",
    email: "",
    role_id: "",
    phone_number: "",
    manager_id: "",
    commercial_or_residential: "residential",
    team_ids: [],
  });

  // State management for allowed states
  const [residentialStates, setResidentialStates] = useState<string[]>([
    "AR",
    "AZ",
    "CT",
    "CO",
    "IA",
    "KS",
    "KY",
    "LA",
    "MA",
    "ME",
    "MO",
    "NC",
    "NH",
    "NJ",
    "OH",
    "OK",
    "PA",
    "RI",
    "SC",
    "TX",
    "VT",
    "MD",
    "FL",
  ]);
  const [commercialStates, setCommercialStates] = useState<string[]>([
    "CT",
    "IL",
    "MD",
    "MA",
    "NJ",
    "PA",
    "RI",
    "VA",
    "DC",
    "WV",
    "ME",
    "NH",
    "VT",
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [installerPartners, setInstallerPartners] = useState<
    Array<{ id: string; name: string; partnerType?: string | null }>
  >([]);

  // Helper functions to filter teams by type - memoized to prevent infinite re-renders
  // Every team and installer partner this person belongs to. Saving REPLACES all of their
  // memberships with what the form sends, so the list carries every one through.
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [hiddenTeamIds, setHiddenTeamIds] = useState<string[]>([]);
  const [adding, setAdding] = useState<{ type: string; id: string } | null>(null);
  const membershipsTouched = useRef(false);

  // Fetch installer partners
  useEffect(() => {
    const fetchInstallerPartners = async () => {
      try {
        const { data, error } = await supabase
          .from("installer_partners")
          .select("id, company_name, partner_type")
          .eq("is_active", true)
          .order("company_name");

        if (error) throw error;

        const formattedPartners = data.map((partner) => ({
          id: partner.id,
          name: partner.company_name,
          partnerType: partner.partner_type,
        }));

        setInstallerPartners(formattedPartners);
      } catch (error) {
        console.error("Error fetching installer partners:", error);
      }
    };

    fetchInstallerPartners();
  }, []);

  // Fetch user's allowed states when editing
  useEffect(() => {
    const fetchUserStates = async () => {
      // Default states
      const defaultResidentialStates = [
        "AR",
        "AZ",
        "CT",
        "CO",
        "IA",
        "KS",
        "KY",
        "LA",
        "MA",
        "ME",
        "MO",
        "NC",
        "NH",
        "NJ",
        "OH",
        "OK",
        "PA",
        "RI",
        "SC",
        "TX",
        "VT",
        "MD",
        "FL",
      ];
      const defaultCommercialStates = [
        "CT",
        "IL",
        "MD",
        "MA",
        "NJ",
        "PA",
        "RI",
        "VA",
        "DC",
        "WV",
        "ME",
        "NH",
        "VT",
      ];

      // Reset to defaults first
      setResidentialStates(defaultResidentialStates);
      setCommercialStates(defaultCommercialStates);

      if (!isEdit || !initialData?.id || !currentTenant?.tenant_id || !open)
        return;

      try {
        const { data, error } = await supabase
          .from("allowed_user_states")
          .select("residential_states, commercial_states")
          .eq("user_id", initialData.id)
          .eq("tenant_id", currentTenant.tenant_id)
          .single();

        if (error && error.code !== "PGRST116") {
          // PGRST116 is "not found"
          console.error("Error fetching user states:", error);
          return;
        }

        if (data) {
          // Use user's custom states if they exist and are not empty
          if (
            data.residential_states &&
            Array.isArray(data.residential_states) &&
            data.residential_states.length > 0
          ) {
            setResidentialStates(data.residential_states);
          }
          if (
            data.commercial_states &&
            Array.isArray(data.commercial_states) &&
            data.commercial_states.length > 0
          ) {
            setCommercialStates(data.commercial_states);
          }
        }
      } catch (error) {
        console.error("Error fetching user states:", error);
      }
    };

    fetchUserStates();
  }, [isEdit, initialData?.id, currentTenant?.tenant_id, open]);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        first_name: initialData.first_name || "",
        last_name: initialData.last_name || "",
        email: initialData.email || "",
        // USR-1.3: the role the TABLE shows, not the legacy column. Empty when the
        // person holds several here, so the choice has to be made deliberately.
        // Stringified because `User.role_id` is a string and the select compares by
        // string; `?? ""` alone would leave a number here and fail the type.
        role_id: seededRole.roleId !== null ? String(seededRole.roleId) : "",
        phone_number: initialData.phone_number || "",
        manager_id: initialData.manager_id || "",
        commercial_or_residential:
          initialData.commercial_or_residential || "residential",
        team_ids: initialData.team_ids
          ? initialData.team_ids.map((id) => String(id))
          : [], // Ensure team_ids is always an array of strings
      }));

      // Reset password reset state when editing a different user
      setShowPasswordReset(false);
      setNewPassword("");
    } else {
      // Reset form for new user creation
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        role_id: "",
        phone_number: "",
        manager_id: "",
        commercial_or_residential: "residential",
        team_ids: [],
      });
    }
    // `seededRole` is memoized on initialData's own fields, so it is stable whenever
    // initialData is; it is listed because the effect genuinely reads it (USR-1.3).
  }, [initialData, seededRole, open]); // Reset when dialog opens/closes

  // Load memberships for the person being edited once the team and partner lists arrive.
  // Stops recomputing as soon as the admin changes the list, so a late load cannot undo an edit.
  useEffect(() => {
    if (!open) return;
    if (!isEdit || !initialData) {
      membershipsTouched.current = false;
      setMemberships([]);
      setHiddenTeamIds([]);
      setAdding(null);
      return;
    }
    if (membershipsTouched.current) return;
    const { visible, hidden } = membershipsFromUser({
      userTeamIds: (initialData.user_teams ?? []).map((ut) => String(ut.team_id)),
      installerPartnerIds: (initialData.installer_partners ?? []).map((ip) => String(ip.id)),
      teams: allTeams.map((t) => ({ id: String(t.id), name: t.name, type: t.type ?? "SALES" })),
      partners: installerPartners,
    });
    setMemberships(visible);
    setHiddenTeamIds(hidden);
  }, [open, isEdit, initialData, allTeams, installerPartners]);

  useEffect(() => {
    if (!open) {
      membershipsTouched.current = false;
      setAdding(null);
    }
  }, [open]);

  const teamOptions = useMemo(
    () => allTeams.map((t) => ({ id: String(t.id), name: t.name, type: t.type ?? "SALES" })),
    [allTeams]
  );
  const addableTypes = useMemo(
    () => addableMembershipTypes(teamOptions, installerPartners, memberships),
    [teamOptions, installerPartners, memberships]
  );

  const clearTeamsError = () => {
    if (errors.teams) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.teams;
        return next;
      });
    }
  };

  const confirmAddMembership = () => {
    if (!adding?.id) return;
    const option = addableOptions(adding.type, teamOptions, installerPartners, memberships).find(
      (o) => o.id === adding.id
    );
    if (!option) return;
    membershipsTouched.current = true;
    setMemberships((prev) => addMembership(prev, option));
    setAdding(null);
    clearTeamsError();
  };

  const removeMembership = (id: string) => {
    membershipsTouched.current = true;
    setMemberships((prev) => prev.filter((m) => m.id !== id));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone_number || !formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else {
      // Remove all non-digit characters to check length
      const digitsOnly = formData.phone_number.replace(/\D/g, "");
      if (digitsOnly.length < 10) {
        newErrors.phone_number = `Phone number needs ${10 - digitsOnly.length} more digits`;
      } else if (digitsOnly.length > 10) {
        newErrors.phone_number = "Phone number has too many digits";
      }
    }

    /**
     * USR-1.3. Deliberately narrow: this fires ONLY when the person holds several
     * roles in this account, which is when `effectiveRoleId` refuses to seed and
     * leaves the select empty on purpose.
     *
     * Saving it empty would be actively harmful rather than merely incomplete.
     * `update-admin-user` coerces "" through `parseInt` to NaN and writes
     * `users.role_id = NULL`, while Step 5b treats a null desired role as a no-op and
     * leaves the `user_roles` rows alone. The result is a user whose two role systems
     * disagree, which is precisely the state `20260814g` was written to clear.
     *
     * Everyone else keeps today's behaviour. `role_id` has never been validated here
     * despite the "Role *" label, and making it required across the board would block
     * edits to users who legitimately hold no legacy role.
     */
    if (!roleOnRecord && seededRole.ambiguous && !formData.role_id) {
      newErrors.role_id =
        "This person holds several roles in this account. Choose the one they should keep - saving will remove the others.";
    }

    // Only require team for new users, not for editing existing users
    if (
      !isEdit &&
      memberships.length === 0
    ) {
      newErrors.teams =
        "Add them to at least one team";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");

    // Format as (###) ###-####
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    let processedValue = value;

    // Format phone number as user types
    if (name === "phone_number") {
      processedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Handle email validation
    if (name === "email" && onEmailChange) {
      setIsEmailChecking(true);
      onEmailChange(value).finally(() => setIsEmailChecking(false));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Validate required fields when user leaves the field
    if (name === "first_name" && !value.trim()) {
      setErrors((prev) => ({
        ...prev,
        first_name: "First name is required",
      }));
    }

    if (name === "last_name" && !value.trim()) {
      setErrors((prev) => ({
        ...prev,
        last_name: "Last name is required",
      }));
    }

    if (name === "email") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          email: "Email is required",
        }));
      } else if (!/\S+@\S+\.\S+/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email",
        }));
      }
    }

    // Validate phone number when user leaves the field
    if (name === "phone_number") {
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length === 0) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "Phone number is required",
        }));
      } else if (digitsOnly.length < 10) {
        setErrors((prev) => ({
          ...prev,
          phone_number: `Phone number needs ${10 - digitsOnly.length} more digits`,
        }));
      } else if (digitsOnly.length > 10) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "Phone number has too many digits",
        }));
      }
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const saveUserStates = async (userIdOverride?: string) => {
    if (!currentTenant?.tenant_id) return;

    const defaultResidentialStates = [
      "AR",
      "AZ",
      "CT",
      "CO",
      "IA",
      "KS",
      "KY",
      "LA",
      "MA",
      "ME",
      "MO",
      "NC",
      "NH",
      "NJ",
      "OH",
      "OK",
      "PA",
      "RI",
      "SC",
      "TX",
      "VT",
      "MD",
      "FL",
    ];
    const defaultCommercialStates = [
      "CT",
      "IL",
      "MD",
      "MA",
      "NJ",
      "PA",
      "RI",
      "VA",
      "DC",
      "WV",
      "ME",
      "NH",
      "VT",
    ];

    // Check if states differ from defaults
    const residentialStatesDiffer =
      JSON.stringify([...residentialStates].sort()) !==
      JSON.stringify([...defaultResidentialStates].sort());
    const commercialStatesDiffer =
      JSON.stringify([...commercialStates].sort()) !==
      JSON.stringify([...defaultCommercialStates].sort());

    // Get the user ID (either from override, initial data for edit mode, or try to get from email for new users)
    let userId = userIdOverride || (isEdit ? initialData?.id : null);

    // For new users, try to get the user ID by email
    if (!userId && !isEdit && formData.email) {
      try {
        // SUB-1.47 · F-106. This read `user_profiles` (columns: `id, email,
        // name`) for `user_id`, filtered on `tenant_id`. Neither column exists
        // on that matview, so the lookup has always failed and `userId` has
        // always stayed null here. `users` carries id, email and tenant_id, so
        // the tenant filter is now a real filter rather than a 42703.
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("id")
          .eq("email", formData.email)
          .eq("tenant_id", currentTenant.tenant_id)
          .single();

        if (!userError && userData) {
          userId = userData.id;
        }
      } catch (error) {
        console.error("Error finding user by email:", error);
      }
    }

    if (!userId) {
      return;
    }

    try {
      if (residentialStatesDiffer || commercialStatesDiffer) {
        // Save custom states
        const { error } = await supabase.from("allowed_user_states").upsert(
          {
            user_id: userId,
            tenant_id: currentTenant.tenant_id,
            residential_states: residentialStates,
            commercial_states: commercialStates,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "user_id,tenant_id",
          }
        );

        if (error) {
          console.error("Error saving user states:", error);
          toast({
            title: "Warning",
            description:
              "User created successfully but state preferences could not be saved.",
            variant: "destructive",
          });
        }
      } else {
        // States match defaults, remove any existing custom record
        const { error } = await supabase
          .from("allowed_user_states")
          .delete()
          .eq("user_id", userId)
          .eq("tenant_id", currentTenant.tenant_id);

        if (error && error.code !== "PGRST116") {
          // Ignore "not found" errors
          console.error("Error removing user states:", error);
        }
      }
    } catch (error) {
      console.error("Error managing user states:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Show toast with validation errors
      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length > 0) {
        toast({
          title: "Please complete required fields",
          description: errorMessages.join(" • "),
          variant: "destructive",
        });
      }
      return;
    }

    // Validate password reset if enabled
    if (showPasswordReset && newPassword.length < 8) {
      toast({
        title: "Error",
        description: "Password must be at least 8 characters long",
        variant: "destructive",
      });
      return;
    }

    const selectedTeamIds = membershipsToTeamIds(memberships, hiddenTeamIds);

    // Include password in submission data if password reset is enabled
    // Also include states for new users
    const submissionData = {
      ...formData,
      team_ids: selectedTeamIds,
      ...(showPasswordReset && newPassword && { password: newPassword }),
      ...(!isEdit && {
        residentialStates,
        commercialStates,
      }),
    };

    // RBAC-4.1: the Role card owns the role on this record (see `roleOnRecord`).
    if (roleOnRecord) delete (submissionData as { role_id?: unknown }).role_id;

    const result = await onSubmit(submissionData);

    if (result.success) {
      // For edit mode, save allowed states separately
      if (isEdit) {
        await saveUserStates();
      }
      // For new users, states are saved in the createUser function in useUsers hook

      onOpenChange(false);
      // Reset form on successful submission for create mode
      if (!isEdit) {
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          role_id: "",
          phone_number: "",
          team_ids: [],
          manager_id: "",
          commercial_or_residential: "residential",
        });
        // Reset states to defaults for new users
        setResidentialStates([
          "AR",
          "AZ",
          "CT",
          "CO",
          "IA",
          "KS",
          "KY",
          "LA",
          "MA",
          "ME",
          "MO",
          "NC",
          "NH",
          "NJ",
          "OH",
          "OK",
          "PA",
          "RI",
          "SC",
          "TX",
          "VT",
          "MD",
          "FL",
        ]);
        setCommercialStates([
          "CT",
          "IL",
          "MD",
          "MA",
          "NJ",
          "PA",
          "RI",
          "VA",
          "DC",
          "WV",
          "ME",
          "NH",
          "VT",
        ]);
      }

      // Reset password reset state on successful submission
      if (showPasswordReset) {
        setShowPasswordReset(false);
        setNewPassword("");
        toast({
          title: "Success",
          description:
            "Password has been reset. User will be prompted to change it on next login.",
        });
      }
    }
  };

  return (
    <SlidePanel
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? "Edit User" : "Add User"}
      width="xl"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
      footer={
        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !formData.first_name ||
                      !formData.last_name ||
                      !formData.email ||
                      !formData.phone_number ||
                      !/^\d{10}$/.test(
                        formData.phone_number.replace(/\D/g, "")
                      ) ||
                      (!isEdit && memberships.length === 0) ||
                      (showPasswordReset && newPassword.length < 8)
                    }
                    onClick={handleSubmit}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isEdit ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{isEdit ? "Update User" : "Add User"}</>
                    )}
                  </Button>
                </div>
              </TooltipTrigger>
              {(isSubmitting ||
                !formData.first_name ||
                !formData.last_name ||
                !formData.email ||
                !formData.phone_number ||
                !/^\d{10}$/.test(formData.phone_number.replace(/\D/g, "")) ||
                (!isEdit && memberships.length === 0) ||
                (showPasswordReset && newPassword.length < 8)) && (
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-1">
                    <p className="font-semibold">
                      Please complete the following:
                    </p>
                    <ul className="text-xs space-y-0.5">
                      {!formData.first_name && (
                        <li>• First name is required</li>
                      )}
                      {!formData.last_name && <li>• Last name is required</li>}
                      {!formData.email && <li>• Email address is required</li>}
                      {!formData.phone_number && (
                        <li>• Phone number is required</li>
                      )}
                      {formData.phone_number &&
                        !/^\d{10}$/.test(
                          formData.phone_number.replace(/\D/g, "")
                        ) && <li>• Phone number must be 10 digits</li>}
                      {!isEdit && memberships.length === 0 && (
                          <li>• At least one team assignment is required</li>
                        )}
                      {showPasswordReset && newPassword.length < 8 && (
                        <li>• Password must be at least 8 characters</li>
                      )}
                    </ul>
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 py-4">
          {/* Contact Information */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-sm font-medium text-foreground mb-3 block">
              User Information
            </Label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder="Enter first name"
                    className="w-full"
                  />
                  {errors.first_name && (
                    <p className="text-sm text-destructive">
                      {errors.first_name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder="Enter last name"
                    className="w-full"
                  />
                  {errors.last_name && (
                    <p className="text-sm text-destructive">
                      {errors.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting || isEdit}
                    placeholder="Enter email address"
                    className={isEdit ? "bg-muted w-full" : "w-full"}
                    autoComplete="off"
                  />
                  {isEmailChecking && (
                    <div className="absolute right-2 top-2.5">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
                {addUserError && (
                  <p className="text-sm text-destructive">{addUserError}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone *</Label>
                  <Input
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder="(123) 456-7890"
                    className="w-full"
                  />
                  {errors.phone_number && (
                    <p className="text-sm text-destructive">
                      {errors.phone_number}
                    </p>
                  )}
                </div>

                {!roleOnRecord && (
                <div className="space-y-2">
                  <Label htmlFor="role_id">Role *</Label>
                  <SearchableSelect
                    id="role_id"
                    value={formData.role_id}
                    onValueChange={(value) =>
                      handleSelectChange("role_id", value)
                    }
                    options={roleOptions.map((role) => ({
                      label: role.name,
                      value: role.id,
                    }))}
                    placeholder="Select role"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                  {errors.role_id && (
                    <p className="text-sm text-destructive">{errors.role_id}</p>
                  )}
                </div>
                )}
              </div>

              {roleOnRecord && initialData?.id && (
                <div className="space-y-2">
                  <Label>Role</Label>
                  <PersonRoleCard
                    userId={initialData.id}
                    onChanged={(roleId) => {
                      setFormData((prev) => ({ ...prev, role_id: String(roleId) }));
                      onRoleChanged?.();
                    }}
                    onRemoved={() => {
                      onRoleChanged?.();
                      onAccessRemoved?.();
                    }}
                  />
                </div>
              )}

              {/*
                COM-R.170 · what handing somebody this role lets them do with money.
                ⚠️ OUTSIDE the two-column grid deliberately. Inside the Role cell it
                rendered in a ~250px column and every explanation wrapped to four
                lines: legible in the source, cramped on the screen.
              */}
              <CommissionRolePermissionsNote roleId={formData.role_id} />
            </div>
          </div>

          {/* Team Assignments */}
          <div className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm font-medium text-foreground">
                Teams {!isEdit && "*"}
                {!isEdit && (
                  <span className="text-xs font-normal text-muted-foreground ml-2">(at least one required)</span>
                )}
              </Label>
              {!adding && addableTypes.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAdding({ type: addableTypes[0].value, id: "" })}
                  disabled={isSubmitting}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add to team
                </Button>
              )}
            </div>

            {memberships.length > 0 ? (
              <ul className="divide-y divide-border rounded-md border border-border">
                {memberships.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 px-3 py-2.5">
                    <span
                      className={cn(
                        "inline-block min-w-[4.5rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold",
                        TEAM_TYPE_BADGE_CLASS[
                          m.type === INSTALLER_PARTNER ? "INSTALLER" : m.type === EPC_PARTNER ? "EPC" : m.type
                        ] ??
                          "bg-muted text-foreground"
                      )}
                    >
                      {membershipTypeLabel(m.type)}
                    </span>
                    <span className="flex-1 text-sm font-medium text-foreground">{m.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove from ${m.name}`}
                      onClick={() => removeMembership(m.id)}
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              !adding && <p className="text-sm text-muted-foreground">Not on any team yet.</p>
            )}

            {adding && (
              <div className="rounded-md border border-dashed border-border bg-muted/40 p-3 space-y-2">
                <div className="grid grid-cols-[10rem_minmax(0,1fr)] gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="add_team_type" className="text-xs">Type</Label>
                    <SearchableSelect
                      id="add_team_type"
                      value={adding.type}
                      onValueChange={(value) => setAdding({ type: value, id: "" })}
                      options={addableTypes}
                      placeholder="Type"
                      className="w-full"
                      inDialog={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="add_team_id" className="text-xs">
                      {adding.type === INSTALLER_PARTNER || adding.type === EPC_PARTNER ? "Company" : "Team"}
                    </Label>
                    <SearchableSelect
                      id="add_team_id"
                      value={adding.id}
                      onValueChange={(value) => setAdding((prev) => (prev ? { ...prev, id: value } : prev))}
                      options={addableOptions(adding.type, teamOptions, installerPartners, memberships).map((o) => ({
                        label: o.name,
                        value: o.id,
                      }))}
                      placeholder="Choose…"
                      className="w-full"
                      inDialog={false}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(null)}>
                    Cancel
                  </Button>
                  <Button type="button" size="sm" onClick={confirmAddMembership} disabled={!adding.id}>
                    Add
                  </Button>
                </div>
              </div>
            )}

            {errors.teams && <p className="text-sm text-destructive">{errors.teams}</p>}

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="manager_id">Manager</Label>
                  <SearchableSelect
                    id="manager_id"
                    value={formData.manager_id || ""}
                    onValueChange={(value) => {
                      handleSelectChange("manager_id", value);
                    }}
                    options={managers.map((manager) => ({
                      label: `${manager.first_name} ${manager.last_name}`,
                      value: manager.id,
                    }))}
                    placeholder="Select manager"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                </div>
            </div>
          </div>

          {/* Password Reset Section for Admin/Superadmin */}
          {isEdit && canResetPassword && (
            <div className="space-y-2">
              <Label>Password Reset</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSendResetEmail}
                  disabled={isSubmitting || isSendingResetEmail || !initialData?.email}
                >
                  {isSendingResetEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Email reset link
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordReset(!showPasswordReset)}
                  disabled={isSubmitting}
                >
                  {showPasswordReset ? "Cancel" : "Set password manually"}
                </Button>
              </div>
              {!showPasswordReset && (
                <p className="text-xs text-muted-foreground">
                  The email sends {initialData?.email} a link to choose their own password.
                </p>
              )}

              {showPasswordReset && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      className="pr-20"
                    />
                    <div className="absolute inset-y-0 right-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-sm text-muted-foreground hover:underline"
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(newPassword);
                          toast({
                            title: "Success",
                            description: "Password copied!",
                            duration: 1000,
                          });
                        }}
                        className="text-sm text-muted-foreground hover:underline"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    User will be prompted to change password on next login
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Primary Goal Section */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-sm font-medium text-foreground mb-3 block">
              Primary Goal
            </Label>
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <input
                  id="residential"
                  name="commercial_or_residential"
                  type="radio"
                  value="residential"
                  checked={formData.commercial_or_residential === "residential"}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      commercial_or_residential: "residential",
                    }))
                  }
                  className="h-4 w-4 text-primary focus:ring-primary border-input"
                  disabled={isSubmitting}
                />
                <Label htmlFor="residential" className="text-sm cursor-pointer">
                  Residential
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="commercial"
                  name="commercial_or_residential"
                  type="radio"
                  value="commercial"
                  checked={formData.commercial_or_residential === "commercial"}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      commercial_or_residential: "commercial",
                    }))
                  }
                  className="h-4 w-4 text-primary focus:ring-primary border-input"
                  disabled={isSubmitting}
                />
                <Label htmlFor="commercial" className="text-sm cursor-pointer">
                  Commercial
                </Label>
              </div>
            </div>
          </div>

          {/* Allowed States Section */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-4">
              Allowed States
            </h3>
            <UserStatesSelector
              residentialStates={residentialStates}
              commercialStates={commercialStates}
              onResidentialStatesChange={setResidentialStates}
              onCommercialStatesChange={setCommercialStates}
              disabled={isSubmitting}
            />
          </div>

          {/* Documents Section (edit mode only) */}
          {isEdit && initialData?.id && (
            <UserDocuments
              userId={initialData.id}
              disabled={isSubmitting}
            />
          )}
        </div>
      </form>
    </SlidePanel>
  );
};
